import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  FileText, 
  Save, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  History,
  Eye,
  Edit2,
  X
} from 'lucide-react';

interface GradingGuide {
  id: string;
  version: string;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  notes: string | null;
}

export default function CallGradingGuidePage() {
  const [guides, setGuides] = useState<GradingGuide[]>([]);
  const [activeGuide, setActiveGuide] = useState<GradingGuide | null>(null);
  const [editingGuide, setEditingGuide] = useState<GradingGuide | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state for new/edit
  const [formVersion, setFormVersion] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formNotes, setFormNotes] = useState('');

  useEffect(() => {
    loadGuides();
  }, []);

  const loadGuides = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('call_grading_guide')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setGuides(data || []);
      const active = data?.find(g => g.is_active);
      setActiveGuide(active || null);
      setEditingGuide(active || null);
    } catch (err: any) {
      setError(`Failed to load grading guides: ${err.message}`);
      console.error('Error loading guides:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!editingGuide) return;
    setFormVersion(editingGuide.version);
    setFormContent(editingGuide.content);
    setFormNotes(editingGuide.notes || '');
    setIsEditing(true);
    setIsCreatingNew(false);
  };

  const handleCreateNew = () => {
    setFormVersion('');
    setFormContent(activeGuide?.content || '');
    setFormNotes('');
    setIsCreatingNew(true);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreatingNew(false);
    setFormVersion('');
    setFormContent('');
    setFormNotes('');
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!formVersion || !formContent) {
        setError('Version and content are required');
        return;
      }

      if (isCreatingNew) {
        const { error: insertError } = await supabase
          .from('call_grading_guide')
          .insert({
            version: formVersion,
            content: formContent,
            notes: formNotes || null,
            is_active: false
          });

        if (insertError) throw insertError;

        setSuccess(`Created new version: ${formVersion}`);
        await loadGuides();
        handleCancel();
      } else if (isEditing && editingGuide) {
        const { error: updateError } = await supabase
          .from('call_grading_guide')
          .update({
            version: formVersion,
            content: formContent,
            notes: formNotes || null
          })
          .eq('id', editingGuide.id);

        if (updateError) throw updateError;

        setSuccess(`Updated version: ${formVersion}`);
        await loadGuides();
        handleCancel();
      }
    } catch (err: any) {
      setError(`Failed to save: ${err.message}`);
      console.error('Error saving guide:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (guideId: string, version: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { error: updateError } = await supabase
        .from('call_grading_guide')
        .update({ is_active: true })
        .eq('id', guideId);

      if (updateError) throw updateError;

      setSuccess(`Set ${version} as active grading guide`);
      await loadGuides();
    } catch (err: any) {
      setError(`Failed to set active version: ${err.message}`);
      console.error('Error setting active:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectVersion = (guide: GradingGuide) => {
    setEditingGuide(guide);
    setIsEditing(false);
    setIsCreatingNew(false);
    setShowHistory(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Master Call Grading Guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Master Call Grading Guide</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage AI grading rules and criteria for call evaluations
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <History className="h-4 w-4 mr-2" />
                {showHistory ? 'Hide' : 'Show'} History
              </button>
              <button
                onClick={handleCreateNew}
                disabled={isEditing || isCreatingNew}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Version
              </button>
            </div>
          </div>

          {activeGuide && !showHistory && (
            <div className="mt-4 flex items-center space-x-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="font-medium text-gray-900">Active Version: {activeGuide.version}</span>
              <span className="text-gray-500">
                • Last updated {new Date(activeGuide.updated_at).toLocaleDateString()}
              </span>
            </div>
          )}

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          )}
        </div>

        {showHistory && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Version History</h3>
            <div className="space-y-2">
              {guides.map(guide => (
                <div
                  key={guide.id}
                  className={`p-4 border rounded-lg cursor-pointer transition ${
                    editingGuide?.id === guide.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleSelectVersion(guide)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900">{guide.version}</span>
                      {guide.is_active && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      )}
                    </div>
                    <Clock className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(guide.created_at).toLocaleString()}
                  </p>
                  {guide.notes && (
                    <p className="text-sm text-gray-500 mt-2">{guide.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg">
          {(isEditing || isCreatingNew) ? (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isCreatingNew ? 'Create New Version' : 'Edit Version'}
                </h3>
                <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version Number
                </label>
                <input
                  type="text"
                  value={formVersion}
                  onChange={(e) => setFormVersion(e.target.value)}
                  placeholder="e.g., v1.1, v2.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Change Notes (Optional)
                </label>
                <input
                  type="text"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Brief description of changes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grading Guide Content
                </label>
                <textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Paste the Master Call Grading Guide content here..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formContent.length.toLocaleString()} characters
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formVersion || !formContent}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : editingGuide ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{editingGuide.version}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Updated {new Date(editingGuide.updated_at).toLocaleString()}
                  </p>
                  {editingGuide.notes && (
                    <p className="text-sm text-gray-500 mt-1">{editingGuide.notes}</p>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {!editingGuide.is_active && (
                    <button
                      onClick={() => handleSetActive(editingGuide.id, editingGuide.version)}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-green-600 rounded-md text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Set as Active
                    </button>
                  )}
                  <button
                    onClick={handleEdit}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Content Preview</h4>
                  <span className="text-xs text-gray-500">
                    {editingGuide.content.length.toLocaleString()} characters
                  </span>
                </div>
                <div className="bg-white border border-gray-200 rounded-md p-4 max-h-96 overflow-y-auto">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                    {editingGuide.content}
                  </pre>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <Eye className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p>Select a version to view or create a new one</p>
            </div>
          )}
        </div>
    </div>
  );
}
